import React, { memo, useCallback } from 'react';
import { IconDownload, IconEyeOutline, IconTrashOutline } from '../../icons';
import { FilePreviewVariant, useFilePreview } from './FilePreview';
import cn from '../../utils/cn';
import Button, { ButtonSize } from '../buttons/button/Button';

const Root = memo(({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={cn('flex items-center gap-2.5', className)} {...props} />
));

Root.displayName = 'FilePreview.Actions.Root';

const ViewButton = memo(
  ({ ...props }: Omit<React.ComponentPropsWithoutRef<'button'>, 'children'>) => {
    const { variant, mimeType, fileSrc } = useFilePreview();

    const handleView = useCallback(() => {
      window.open(fileSrc, '_blank');
    }, [fileSrc]);

    if (
      variant === FilePreviewVariant.DOCUMENT &&
      mimeType !== 'application/pdf' &&
      !mimeType?.startsWith('image/')
    ) {
      return null;
    }

    return (
      <Button size={ButtonSize.ICON} onClick={handleView} aria-label="Voir" {...props}>
        <IconEyeOutline />
      </Button>
    );
  },
);

ViewButton.displayName = 'FilePreview.Actions.ViewButton';

const DownloadButton = memo(
  ({ ...props }: Omit<React.ComponentPropsWithoutRef<'button'>, 'children'>) => {
    const { fileName, fileSrc } = useFilePreview();

    const handleDownload = useCallback(() => {
      if (!fileName) {
        return;
      }

      const link = document.createElement('a');
      link.href = fileSrc;
      link.download = fileName;
      document.body.appendChild(link);

      link.click();
      document.body.removeChild(link);
    }, [fileName, fileSrc]);

    if (!fileName) {
      return null;
    }

    return (
      <Button
        size={ButtonSize.ICON}
        onClick={handleDownload}
        aria-label="Télécharger"
        {...props}
      >
        <IconDownload />
      </Button>
    );
  },
);

DownloadButton.displayName = 'FilePreview.Actions.DownloadButton';

type RemoveButtonProps = Omit<React.ComponentPropsWithoutRef<'button'>, 'children'>;

const RemoveButton = memo(({ ...props }: RemoveButtonProps) => {
  const { onRemove } = useFilePreview();

  if (!onRemove) {
    return null;
  }

  return (
    <Button size={ButtonSize.ICON} onClick={onRemove} aria-label="Supprimer" {...props}>
      <IconTrashOutline />
    </Button>
  );
});

RemoveButton.displayName = 'FilePreview.Actions.RemoveButton';

const FilePreviewActions = memo(
  ({ ...props }: React.ComponentPropsWithoutRef<typeof Root>) => (
    <Root {...props}>
      <ViewButton />
      <DownloadButton />
      <RemoveButton />
    </Root>
  ),
);

FilePreviewActions.displayName = 'FilePreview.Actions';

const FilePreviewActionsCompound = Object.assign(FilePreviewActions, {
  Root,
  ViewButton,
  DownloadButton,
  RemoveButton,
});

export default FilePreviewActionsCompound;
